import { getModule } from '@/hooks/useRouteScope';
import { PREFIX_URL, PREFIX_WS_URL } from '@/service/constants';
import { GROUPS } from '@/service/groups';
import { getPathnameForContextParsing } from '@/utils/pathContext';

// 这些函数需要从index.ts导入，但为了避免循环依赖，我们在这里重新定义
const getUrlWorkspace = () => {
  const [, var0, name1] = getPathnameForContextParsing().split('/');
  const obj = {
    [var0]: name1,
  };
  return obj.console || obj.wsp || obj.ws;
};

const getWorkspacePrefix = (pre = '') => {
  let res = pre;
  const workspace = getUrlWorkspace();
  const arr = getPathnameForContextParsing().split('/');
  if (workspace) {
    res = `${pre}/workspaces/${arr[2]}`;
  }
  return res;
};

const getClusterPrefix = () => {
  const [, var0, name1, var1, name2, var2, name3] = getPathnameForContextParsing().split('/');
  const obj = {
    [var0]: name1,
    [var1]: name2,
    [var2]: name3,
    [name1]: var1, // 数据中心分享
  };
  obj.cluster = obj.cluster || obj['data-center'] || obj.dc;
  const prefix = obj.subcluster ? `clusters/${obj.cluster}/subclusters/${obj.subcluster}` : obj.cluster ? `dc/${obj.cluster}` : '';
  const currCluster = obj.subcluster || obj.cluster || obj.dc;
  return {
    prefix,
    cluster: currCluster,
    dataCenter: obj.cluster,
    subCluster: obj.subcluster,
  };
};

// 公共页面： dc/:cluster 获取 dc/ 后面的数据中心 eg： 分享大屏之后，路由格式与 datacenter 不同，需要处理
const getDcInPathname = () => {
  const path = getPathnameForContextParsing();
  const match = path.match(/\/dc\/([^/]+)/);
  return match ? match[1] : null;
};

const getCurrCluster = () => getClusterPrefix()?.cluster;
const getCurrDataCenter = () => getClusterPrefix()?.dataCenter;
const getCurrSubcluster = () => getClusterPrefix()?.subCluster;

const dcPrefix = (dc) => (dc ? `/dc/${dc}` : '');

export const PREFIX = {
  vapis: (group: GROUPS) => `${PREFIX_URL}/vapis/${group}/v1alpha1`,
  vapis_dc: (group: GROUPS, dc: string = undefined) => {
    dc = dc || getCurrDataCenter() || getDcInPathname();
    return `${PREFIX_URL}/vapis/${group}/v1alpha1` + dcPrefix(dc);
  },
  vapis_v1_dc0: (group: GROUPS, dc?: string) => {
    dc = dc || getCurrDataCenter();
    return `${PREFIX_URL}/vapis/${group}` + dcPrefix(dc);
  },
  vapis_v1_dc: (group: GROUPS, dc: string = undefined) => {
    dc = dc || getCurrDataCenter();
    return `${PREFIX_URL}/vapis/${group}/v1` + dcPrefix(dc);
  },
  vapis_v1_ws: (group: GROUPS, ws: string = undefined) => {
    ws = ws ? `/workspaces/${ws}` : getWorkspacePrefix();
    return `${PREFIX_URL}/vapis/${group}/v1` + ws;
  },
  vapis_dc_subc_ws: (group: GROUPS, dc: string = undefined, subc: string = undefined, ws: string = undefined) => {
    dc = dc || getCurrDataCenter();
    ws = ws ? `/workspaces/${ws}` : getWorkspacePrefix();
    return `${PREFIX_URL}/vapis/clusters/${dc}/subclusters/${subc}/${group}/v1alpha1` + ws;
  },
  vapis_ws: (group: GROUPS, ws: string = undefined) => {
    ws = ws ? `/workspaces/${ws}` : getWorkspacePrefix();
    return `${PREFIX_URL}/vapis/${group}/v1alpha1` + ws;
  },
  vapis_dc_ws: (group: GROUPS, dc: string = undefined, ws: string = undefined) => {
    dc = dc || getCurrDataCenter() || getDcInPathname();
    ws = ws ? `/workspaces/${ws}` : getWorkspacePrefix();
    return `${PREFIX_URL}/vapis/${group}/v1alpha1` + dcPrefix(dc) + ws;
  },
  local_download: (group, dc: string = undefined) => {
    dc = dc || getCurrDataCenter();
    return `https://infra-dev.vastaitech.com/vapis/${group}/v1alpha1` + dcPrefix(dc);
  },
  vapis_dc_ws_alpha2: (group: GROUPS, dc: string = undefined, ws: string = undefined) => {
    dc = dc || getCurrDataCenter();
    ws = ws ? `/workspaces/${ws}` : getWorkspacePrefix();
    return `${PREFIX_URL}/vapis/${group}/v1alpha2` + dcPrefix(dc) + ws;
  },
  vapis_v1_dc_ws: (group: GROUPS, dc: string = undefined, ws: string = undefined) => {
    dc = dc || getCurrDataCenter();
    ws = ws ? `/workspaces/${ws}` : getWorkspacePrefix();
    return `${PREFIX_URL}/vapis/${group}/v1` + dcPrefix(dc) + ws;
  },
  vapis_v1alpha1_dc: (group: GROUPS, dc?: string) => {
    dc = dc || getCurrDataCenter();
    return `${PREFIX_URL}/vapis/${group}/v1alpha1` + dcPrefix(dc);
  },
  oauth: `${PREFIX_URL}/oauth`,
  oauths: (group: GROUPS) => {
    return `${PREFIX_URL}/oauths/${group}/v1alpha1`;
  },
  oauths_v1alpha1_dc: (group: GROUPS, dc?: string) => {
    dc = dc || getCurrDataCenter();
    return `${PREFIX_URL}/oauths/${group}/v1alpha1` + dcPrefix(dc);
  },
  oauths_dc: (group: GROUPS, dc?: string) => {
    dc = dc || getCurrDataCenter();
    return `${PREFIX_URL}/oauths/${group}` + dcPrefix(dc);
  },
  api_v1_ws: (ws: string = undefined) => {
    ws = ws ? `/workspaces/${ws}` : getWorkspacePrefix();
    return `${PREFIX_URL}/api/v1` + ws;
  },
  api_v1_dc: (dc: string = undefined) => {
    dc = dc || getCurrDataCenter();
    return `${PREFIX_URL}/api` + dcPrefix(dc);
  },
  api_v1_dc_ws: (dc: string = undefined, ws: string = undefined) => {
    dc = dc || getCurrDataCenter();
    ws = ws ? `/workspaces/${ws}` : getWorkspacePrefix();
    return `${PREFIX_URL}/api/v1` + dcPrefix(dc) + ws;
  },
  apis_v2beta2_dc: (group: GROUPS, dc: string = undefined) => {
    dc = dc || getCurrDataCenter();
    return `${PREFIX_URL}/apis/${group}/v2beta2` + dcPrefix(dc);
  },
  apis_v1_dc_ws: (group: GROUPS, dc: string = undefined, ws: string = undefined) => {
    dc = dc || getCurrDataCenter();
    ws = ws ? `/workspaces/${ws}` : getWorkspacePrefix();
    return `${PREFIX_URL}/apis/${group}/v1` + dcPrefix(dc) + ws;
  },
  vapis_v1alpha2_dc_ws: (group: GROUPS, dc: string = undefined, ws: string = undefined) => {
    dc = dc || getCurrDataCenter();
    ws = ws ? `/workspaces/${ws}` : getWorkspacePrefix();
    return `${PREFIX_URL}/vapis/${group}/v1alpha2` + dcPrefix(dc) + ws;
  },
};

export const WS_PREFIX = {
  vapis_dc_ws: (group: GROUPS, dc: string = undefined, ws: string = undefined) => {
    dc = dc || getCurrDataCenter();
    ws = ws ? `/workspaces/${ws}` : getWorkspacePrefix();
    return `${PREFIX_WS_URL}/vapis/${group}/v1alpha1` + dcPrefix(dc) + ws;
  },
  vapis_dc: (group: GROUPS, dc: string = undefined) => {
    dc = dc || getCurrDataCenter();
    return `${PREFIX_WS_URL}/vapis/${group}/v1alpha1` + dcPrefix(dc);
  },
  vapis_v1_dc_ws: (group: GROUPS, dc: string = undefined, ws: string = undefined) => {
    dc = dc || getCurrDataCenter();
    ws = ws ? `/workspaces/${ws}` : getWorkspacePrefix();
    return `${PREFIX_WS_URL}/vapis/${group}/v1` + dcPrefix(dc) + ws;
  },
  vapis_v1_dc: (group: GROUPS, dc: string = undefined) => {
    dc = dc || getCurrDataCenter();
    return `${PREFIX_WS_URL}/vapis/${group}/v1` + dcPrefix(dc);
  },
};

export const getClusterProxyPrefix = (cluster = undefined) => {
  cluster = cluster || getCurrCluster();
  return `${PREFIX.vapis_v1_dc_ws(GROUPS.proxyCluster)}/clusters/${cluster}/proxy`;
};

export const getWsClusterProxyPrefix = (cluster = undefined) => {
  cluster = cluster || getCurrCluster();
  return `${WS_PREFIX.vapis_v1_dc_ws(GROUPS.proxyCluster)}/clusters/${cluster}/proxy`;
};
export const PREFIX_PROXY = {
  vapis: (group: GROUPS, cluster: string = undefined) => {
    const module = getModule();
    if (module == 'cluster' || module == 'wsp') {
      return `${getClusterProxyPrefix(cluster)}/vapis/${group}/v1alpha1`;
    }
    return PREFIX.vapis_dc_ws(group);
  },
  vapis_v1: (group: GROUPS, cluster: string = undefined) => {
    const module = getModule();
    if (module == 'cluster' || module == 'wsp') {
      return `${getClusterProxyPrefix(cluster)}/vapis/${group}/v1`;
    }
    return PREFIX.vapis_v1_dc_ws(group);
  },
  apis_v1: (group: GROUPS, cluster: string = undefined) => {
    const module = getModule();
    if (module == 'cluster' || module == 'wsp') {
      return `${getClusterProxyPrefix(cluster)}/apis/${group}/v1`;
    }
    return PREFIX.apis_v1_dc_ws(group);
  },
  apis_v2beta2: (group: GROUPS, cluster: string = undefined) => {
    return `${getClusterProxyPrefix(cluster)}/apis/${group}/v2beta2`;
  },
  apis_v2: (group: GROUPS, cluster: string = undefined) => {
    return `${getClusterProxyPrefix(cluster)}/apis/${group}/v2`;
  },
  api_v1: (cluster: string = undefined) => {
    const module = getModule();
    if (module == 'cluster' || module == 'wsp') {
      return `${getClusterProxyPrefix(cluster)}/api/v1`;
    }
    return PREFIX.api_v1_dc_ws();
  },
};
